import { useCallback, useEffect, useRef, useState } from "react";
import { BarChart3Icon } from "lucide-react";
import { Card } from "@component/card";
import { EmptyState } from "@component/empty-state";
import { TrainingErrorCard } from "@component/training/training-async-state";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";
import {
    getAiqmSentimentComparison,
    type SentimentAggregateParams,
} from "@service/aiqm/sentiment";
import { getCallSessions } from "@service/aiqm";
import type {
    AiqmSentimentAggregate,
    SentimentComparison,
    SentimentGranularity,
} from "@type/aiqm";
import { GRANULARITY_LABELS } from "@type/aiqm";
import SentimentTrendChart from "./sentiment-trend-chart";
import SentimentThemeBoard from "./sentiment-theme-board";
import SentimentKpiCards from "./sentiment-kpi-cards";
import SentimentDistributionDonut from "./sentiment-distribution-donut";
import SentimentMovers from "./sentiment-movers";
import SentimentFilterBar, {
    type SentimentFilters,
} from "./sentiment-filter-bar";

// Small inline segmented control for granularity. Kept local — it's the only
// place that needs it and the styling matches the page's existing pills.
// Format an ISO date (e.g. "2026-03-05") as "5 March 2026" (single-digit day,
// no zero padding). Parsed as UTC so the day never shifts under local timezone.

function granularityUnit(granularity: SentimentGranularity): string {
    if (granularity === "daily") return "day";
    if (granularity === "weekly") return "week";
    return "month";
}

// Returns the granularity options that make sense for a given date window.
// Short ranges produce too few buckets for coarser groupings to be meaningful:
//   < 14 days  → daily only (weekly/monthly would be 1–2 flat dots)
//   < 60 days  → daily + weekly (monthly would be 1–2 buckets)
//   60+ / all  → all three
function availableGranularities(
    startDate?: string,
    endDate?: string
): SentimentGranularity[] {
    if (!startDate || !endDate) return ["daily", "weekly", "monthly"];
    const dayMs = 86_400_000;
    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    const days = Math.round((end.getTime() - start.getTime()) / dayMs) + 1;
    if (days < 14) return ["daily"];
    if (days < 60) return ["daily", "weekly"];
    return ["daily", "weekly", "monthly"];
}

function GranularityToggle({
    value,
    onChange,
    disabled,
    available,
}: {
    value: SentimentGranularity;
    onChange: (g: SentimentGranularity) => void;
    disabled?: boolean;
    available: SentimentGranularity[];
}) {
    // Single option is auto-selected — no toggle needed, nothing to render.
    if (available.length <= 1) return null;
    return (
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {available.map((g) => (
                <button
                    key={g}
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(g)}
                    className={cn(
                        "rounded-md px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50",
                        value === g
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                    )}
                >
                    {GRANULARITY_LABELS[g]}
                </button>
            ))}
        </div>
    );
}

function SkeletonInsights() {
    return (
        <div className="animate-pulse space-y-4">
            {/* KPI row */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-[92px] rounded-xl bg-gray-100" />
                ))}
            </div>
            {/* Chart + donut */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="h-[360px] rounded-xl bg-gray-100 lg:col-span-2" />
                <div className="h-[360px] rounded-xl bg-gray-100" />
            </div>
            {/* Theme board */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="h-[280px] rounded-xl bg-gray-100" />
                <div className="h-[280px] rounded-xl bg-gray-100" />
            </div>
        </div>
    );
}

export default function AiqmSentimentInsights() {
    const [granularity, setGranularity] = useState<SentimentGranularity>("monthly");
    const [filters, setFilters] = useState<SentimentFilters>({
        startDate: undefined,
        endDate: undefined,
        tags: [],
    });
    const [aggregate, setAggregate] = useState<AiqmSentimentAggregate | null>(null);
    const [comparison, setComparison] = useState<SentimentComparison | null>(null);
    const [resultsReadyTotal, setResultsReadyTotal] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Derived: which granularity tabs to show for the current date window.
    const available = availableGranularities(filters.startDate, filters.endDate);

    // When the date range changes and the current granularity is no longer in the
    // available set (e.g. user picks a 3-day range while on Monthly), auto-switch
    // to the finest available option.
    useEffect(() => {
        if (!available.includes(granularity)) {
            setGranularity(available[0]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.startDate, filters.endDate]);

    // Monotonic request id: rapid granularity/filter changes fire overlapping
    // fetches, and without sequencing an older (slower) response could resolve
    // last and overwrite the newer one. Only the latest request commits state.
    const latestRequestRef = useRef(0);
    // Abort the in-flight request when a newer filter/tag change supersedes it,
    // so stale requests don't linger on the network.
    const abortRef = useRef<AbortController | null>(null);

    const fetchAggregate = useCallback(
        async (params: SentimentAggregateParams) => {
            // Cancel the previous request — its result is now stale.
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            const requestId = ++latestRequestRef.current;
            setLoading(true);
            setError(null);
            try {
                const { current, comparison } = await getAiqmSentimentComparison(
                    params,
                    controller.signal
                );
                if (requestId !== latestRequestRef.current) return;
                setAggregate(current);
                setComparison(comparison);
            } catch (err) {
                // An aborted request is expected (a newer one replaced it) — ignore.
                if (controller.signal.aborted) return;
                if (requestId !== latestRequestRef.current) return;
                const e = err instanceof Error ? err : new Error("Unknown error");
                catchErrorLog(`getAiqmSentimentComparison failed: ${e.message}`);
                setError(e);
            } finally {
                if (requestId === latestRequestRef.current) {
                    setLoading(false);
                }
            }
        },
        []
    );

    // Abort any in-flight request when the Insights view unmounts.
    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        let cancelled = false;
        getCallSessions({ page: 1, per_page: 1, status: "results_ready" })
            .then((res) => {
                if (cancelled) return;
                setResultsReadyTotal(res.total);
            })
            .catch((err) => {
                const e = err instanceof Error ? err : new Error("Unknown error");
                catchErrorLog(`getCallSessions (insights headline count) failed: ${e.message}`);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        fetchAggregate({
            granularity,
            start_date: filters.startDate,
            end_date: filters.endDate,
            tags: filters.tags,
        });
    }, [granularity, filters, fetchAggregate]);

    // Header row: title + description on the left, date/tag filters on the right.
    // Filters stay in every state (error / skeleton / empty / data) so the user
    // can always change the window without being stuck on a dead view.
    const controls = (
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
                <h1 className="text-xl font-semibold text-gray-900">AIQM Insights</h1>
                <p className="mt-0.5 text-sm text-gray-500">
                    Sentiment trends and recurring themes from your analyzed call recordings.
                </p>
            </div>
            <SentimentFilterBar
                value={filters}
                onChange={setFilters}
                disabled={loading}
                resolvedStartDate={aggregate?.start_date}
                resolvedEndDate={aggregate?.end_date}
            />
        </div>
    );

    if (error) {
        return (
            <div className="space-y-4">
                {controls}
                <TrainingErrorCard
                    title="Could not load sentiment insights"
                    message="The error has been reported. Try again or refresh."
                    onRetry={() =>
                        fetchAggregate({
                            granularity,
                            start_date: filters.startDate,
                            end_date: filters.endDate,
                            tags: filters.tags,
                        })
                    }
                />
            </div>
        );
    }

    // Only show the full skeleton on the INITIAL load (no data yet). On a refetch
    // triggered by a granularity/filter change we keep the populated view mounted
    // and show the inline spinner instead — otherwise the granularity toggle (now
    // inside the chart card) would vanish on every click.
    if (!aggregate) {
        return (
            <div className="space-y-4">
                {controls}
                <SkeletonInsights />
            </div>
        );
    }

    // Empty: no calls at all for this granularity/range/campaign.
    if (aggregate.total_calls === 0) {
        return (
            <div className="space-y-4">
                {controls}
                <Card>
                    <EmptyState
                        icon={<BarChart3Icon className="h-10 w-10 text-gray-300" />}
                        title="No calls to analyze yet"
                        description="No analyzed calls match these filters. Try a wider date range, a different campaign, or another time grouping."
                    />
                </Card>
            </div>
        );
    }

    const callsAnalyzedTotal =
        resultsReadyTotal === null
            ? aggregate.total_calls
            : Math.min(aggregate.total_calls, resultsReadyTotal);

    return (
        <div className="space-y-4">
            {controls}

            {/* KPI row — bird's-eye numbers, with period-over-period deltas */}
            <SentimentKpiCards
                aggregate={aggregate}
                comparison={comparison}
                callsAnalyzedTotal={callsAnalyzedTotal}
            />

            {/* Biggest movers — only renders when the BE returned movers */}
            {comparison && comparison.movers.length > 0 && (
                <SentimentMovers movers={comparison.movers} />
            )}

            {/* Trend (2/3) + distribution donut (1/3) */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="p-5 lg:col-span-2">
                    <div className="mb-4 flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-base font-semibold text-gray-800">
                                Sentiment over time
                            </h2>
                            <p className="mt-0.5 text-xs text-gray-500">
                                Net sentiment per {granularityUnit(granularity)} (positive %
                                minus negative %). Above the line is net positive.
                            </p>
                        </div>
                        <GranularityToggle
                            value={granularity}
                            onChange={setGranularity}
                            disabled={loading}
                            available={available}
                        />
                    </div>
                    <SentimentTrendChart
                        trend={aggregate.trend}
                        granularity={granularity}
                    />
                </Card>

                <Card className="flex flex-col p-5">
                    <div className="mb-2">
                        <h2 className="text-base font-semibold text-gray-800">
                            Sentiment mix
                        </h2>
                        <p className="mt-0.5 text-xs text-gray-500">
                            Share of calls by sentiment.
                        </p>
                    </div>
                    <div className="flex flex-1 items-center justify-center">
                        <SentimentDistributionDonut
                            breakdown={aggregate.overall_breakdown}
                            netSentiment={aggregate.overall_net_sentiment}
                        />
                    </div>
                </Card>
            </div>

            {/* Theme board — two columns instead of one endless list */}
            <div>
                <div className="mb-4">
                    <h2 className="text-base font-semibold text-gray-800">
                        Recurring themes
                    </h2>
                    <p className="mt-0.5 text-xs text-gray-400">
                        Topics extracted from your calls, ranked by frequency. Expand a theme to view example calls.
                    </p>
                </div>
                {aggregate.themes.length > 0 ? (
                    <SentimentThemeBoard themes={aggregate.themes} />
                ) : (
                    <Card className="px-4 py-10 text-center text-sm text-gray-400">
                        No recurring themes detected in this range.
                    </Card>
                )}
            </div>
        </div>
    );
}
